import {
  TableRow,
  TableCell,
  IconButton,
  Collapse,
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
} from "@mui/material";
import {
  MovementSettlementHeaderCells,
  MovementTypeHeaderCell,
  UnitValueCell,
} from "components/utils/LiquidationDetails";
import { SETTLEMENT_TYPES } from "constants/index";
import { arsFormatter, roundToThree, formatDateOnlyDMY } from "helpers";
import {
  calculateBillablePallets,
  liquidationArticleVolumeWeight,
  liquidationContainerOf,
  liquidationIncomeDischargeUnitsTotal,
} from "helpers/liquidations";
import React, { useMemo } from "react";
import { BsArrowDown, BsArrowUp } from "react-icons/bs";
import {
  LiquidationAggregation,
  LiquidationSettlementType,
  MovementLiquidation,
} from "types";

export default function Row({
  row,
  liquidation,
  settlementType,
  print,
  hasDetail,
  isAbono = false,
  abonoUnitValue = 0,
  showMinimumAdjustmentColumn = false,
}: {
  row: MovementLiquidation;
  liquidation: LiquidationAggregation;
  settlementType: LiquidationSettlementType;
  print: boolean;
  hasDetail: boolean;
  isAbono?: boolean;
  abonoUnitValue?: number;
  showMinimumAdjustmentColumn?: boolean;
}) {
  const [open, setOpen] = React.useState(print);

  const articles = useMemo(() => {
    return liquidation.liquidation_movement_articles.filter(
      (ma) => ma.movement_id === row.movement_id,
    );
  }, [row, liquidation]);

  const unitsTotal = useMemo(() => {
    return liquidationIncomeDischargeUnitsTotal(
      settlementType,
      row,
      articles,
      liquidation,
    );
  }, [row, liquidation, settlementType, articles]);

  const displayedSubtotal = useMemo(() => {
    if (isAbono) return abonoUnitValue;
    return row.subtotal;
  }, [isAbono, abonoUnitValue, row.subtotal]);

  const isUnits = settlementType.settlement_type === SETTLEMENT_TYPES.UNIDADES;

  const billableValueTotal = useMemo(() => {
    const total = Number(unitsTotal ?? 0);
    const minimumValue = Number(settlementType.minimum_value ?? 0);

    if (isAbono || minimumValue <= 0) return total;

    return total < minimumValue ? minimumValue : total;
  }, [isAbono, settlementType.minimum_value, unitsTotal]);

  const hasMinimumAdjustment = useMemo(() => {
    return billableValueTotal > Number(unitsTotal ?? 0);
  }, [billableValueTotal, unitsTotal]);

  return (
    <React.Fragment>
      <TableRow>
        <TableCell>
          {!print && (
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => setOpen(!open)}
            >
              {open ? <BsArrowUp /> : <BsArrowDown />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>{MovementTypeHeaderCell(row.movement_type_id)}</TableCell>
        <TableCell>{row.movement_id}</TableCell>
        <TableCell>{row.client_document}</TableCell>
        <TableCell>{formatDateOnlyDMY(row.date)}</TableCell>
        <TableCell>{row.associated_client}</TableCell>
        {!isAbono && <TableCell>{roundToThree(unitsTotal || 0)}</TableCell>}
        {showMinimumAdjustmentColumn && (
          <TableCell>
            {hasMinimumAdjustment ? roundToThree(billableValueTotal) : "-"}
          </TableCell>
        )}
        <TableCell>{arsFormatter(displayedSubtotal)}</TableCell>
      </TableRow>
      {hasDetail && (
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 1 }}>
                <Typography variant="h6" gutterBottom component="div">
                  Detalle del Movimiento
                </Typography>
                <Table size="small" aria-label="purchases">
                  <TableHead>
                    <TableRow>
                      {MovementSettlementHeaderCells(
                        settlementType.settlement_type,
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {articles.map((movement_article, index) => {
                      const article = liquidation.liquidation_articles.find(
                        (article) => article.article_id === movement_article.article_id,
                      );
                      const container = liquidationContainerOf(liquidation, article);
                      const articleWeight = Number(article?.weight ?? 0);
                      const articleVolumeWeight = liquidationArticleVolumeWeight(article, container);
                      const selectedWeightValue = Number(movement_article.amount ?? 0) > 0
                        ? Number(movement_article.value ?? 0) / Number(movement_article.amount)
                        : Math.max(articleWeight, articleVolumeWeight);
                      const isWeightSettlement =
                        settlementType.settlement_type === SETTLEMENT_TYPES.PESO ||
                        settlementType.settlement_type === SETTLEMENT_TYPES.PESO_VOLUMETRICO;
                      const unitsPerPallet = Number(article?.units_per_pallet ?? 0);
                      const billablePallets = calculateBillablePallets(
                        Number(movement_article.amount ?? 0),
                        unitsPerPallet,
                      );
                      const isPalletSettlement = settlementType.settlement_type === SETTLEMENT_TYPES.PALLETS;

                      return (
                        <TableRow key={index}>
                          <TableCell>{article?.code}</TableCell>
                          <TableCell>{article?.description}</TableCell>
                          <TableCell>{movement_article.amount}</TableCell>
                          {isWeightSettlement && (
                            <>
                              <TableCell>{roundToThree(articleWeight)}</TableCell>
                              <TableCell>{roundToThree(articleVolumeWeight)}</TableCell>
                              <TableCell>{roundToThree(selectedWeightValue)}</TableCell>
                              <TableCell>{roundToThree(movement_article.value)}</TableCell>
                            </>
                          )}

                          {settlementType.settlement_type !== SETTLEMENT_TYPES.ABONO &&
                            !isUnits &&
                            !isWeightSettlement &&
                            UnitValueCell(
                              settlementType.settlement_type,
                              liquidation,
                              movement_article,
                            )}

                          {settlementType.settlement_type !== SETTLEMENT_TYPES.ABONO && (
                            <>
                              {!isUnits && !isWeightSettlement && (
                                <TableCell>
                                  {roundToThree(isPalletSettlement ? billablePallets : movement_article.value)}
                                </TableCell>
                              )}
                              <TableCell>
                                {arsFormatter(settlementType.calculation)}
                              </TableCell>
                              <TableCell>
                                {arsFormatter(movement_article.subtotal)}
                              </TableCell>
                            </>
                          )}
                        </TableRow>
                      );
                    })} 
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  );
}
